import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Badge } from "@/components/Badge";
import { JOB_ROLE_LABEL, CONTENT_STATUS, TASK_STATUS, label } from "@/lib/meta";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Search, Megaphone, Palette, Video, ListChecks, Send } from "lucide-react";

function RoleCard({ icon: Icon, title, tone, stats }) {
  return (
    <Card className="rounded-lg border-zinc-200 p-4 shadow-sm" data-testid={`role-card-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="mb-3 flex items-center gap-2">
        <div className={`flex h-7 w-7 items-center justify-center rounded-md ${tone}`}><Icon className="h-4 w-4" /></div>
        <h3 className="font-heading text-sm font-semibold">{title}</h3>
      </div>
      <div className="grid grid-cols-2 gap-y-2 text-sm sm:grid-cols-3">
        {stats.map(([l, v, c]) => (
          <div key={l}>
            <div className={`metric-value text-lg font-semibold ${c || "text-zinc-900"}`}>{v}</div>
            <div className="text-[11px] text-zinc-500">{l}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function DrillDialog({ open, onOpenChange, config }) {
  const { data = [], isLoading } = useQuery({
    queryKey: ["drill", config?.key],
    queryFn: async () => (await api.get(config.url, { params: config.params })).data,
    enabled: !!open && !!config,
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{config?.title}</DialogTitle></DialogHeader>
        {isLoading ? (
          <div className="flex h-32 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-400" /></div>
        ) : data.length === 0 ? (
          <div className="py-10 text-center text-sm text-zinc-400">No records</div>
        ) : (
          <div className="space-y-2">
            {data.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-md border border-zinc-100 px-3 py-2" data-testid={`drill-row-${r.id}`}>
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{r.title}</div>
                  <div className="text-xs text-zinc-500">{r.client_name || "—"}{r.date ? ` · ${fmtDate(r.date)}` : ""}{r.due_date ? ` · due ${fmtDate(r.due_date)}` : ""}</div>
                </div>
                {config.kind === "task"
                  ? <Badge color={TASK_STATUS[r.display_status]?.color}>{label(TASK_STATUS, r.display_status)}</Badge>
                  : <Badge color={CONTENT_STATUS[r.status]?.color}>{label(CONTENT_STATUS, r.status)}</Badge>}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function TeamProgress() {
  const [empSearch, setEmpSearch] = useState("");
  const [roleF, setRoleF] = useState("all");
  const [drill, setDrill] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["team-progress"], queryFn: async () => (await api.get("/team-progress")).data });

  if (isLoading || !data)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>;

  const r = data.roles;
  const employees = data.employees.filter((e) => {
    if (roleF !== "all" && e.job_role !== roleF) return false;
    if (empSearch && !e.name.toLowerCase().includes(empSearch.toLowerCase())) return false;
    return true;
  });

  const openDrill = (cfg) => setDrill(cfg);
  const cbtn = "cursor-pointer font-semibold underline-offset-2 hover:underline";

  return (
    <div className="space-y-6" data-testid="team-progress-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Team Progress</h1>
        <p className="mt-1 text-sm text-zinc-500">Factual work counts by role and employee · this week. Click any number to drill down.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <RoleCard icon={Megaphone} title="Social Media" tone="bg-blue-100 text-blue-700" stats={[["Posted Today", r.social_media.posted_today, "text-emerald-600"], ["To Publish", r.social_media.to_publish, "text-amber-600"], ["Stories", r.social_media.stories, "text-blue-600"]]} />
        <RoleCard icon={Palette} title="Graphic Design" tone="bg-purple-100 text-purple-700" stats={[["Pending", r.design.pending, "text-amber-600"], ["In Progress", r.design.in_progress, "text-orange-600"], ["Ready", r.design.ready_for_approval, "text-blue-600"], ["Approved", r.design.approved, "text-emerald-600"], ["Posted", r.design.posted, "text-emerald-600"], ["Carry-fwd", r.design.carry_forward, "text-red-600"]]} />
        <RoleCard icon={Video} title="Video Editing" tone="bg-rose-100 text-rose-700" stats={[["Pending", r.video.pending, "text-amber-600"], ["In Progress", r.video.in_progress, "text-orange-600"], ["Ready", r.video.ready_for_approval, "text-blue-600"], ["Approved", r.video.approved, "text-emerald-600"], ["Posted", r.video.posted, "text-emerald-600"], ["Carry-fwd", r.video.carry_forward, "text-red-600"]]} />
        <RoleCard icon={ListChecks} title="General Tasks" tone="bg-amber-100 text-amber-700" stats={[["Assigned", r.tasks.assigned], ["In Progress", r.tasks.in_progress, "text-orange-600"], ["Completed", r.tasks.completed, "text-emerald-600"], ["Overdue", r.tasks.overdue, "text-red-600"]]} />
        <RoleCard icon={Send} title="Publishing" tone="bg-emerald-100 text-emerald-700" stats={[["Posted Today", r.publishing.posted_today, "text-emerald-600"], ["Awaiting Post", r.publishing.pending, "text-amber-600"], ["Stories", r.publishing.stories, "text-blue-600"]]} />
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <Input placeholder="Search employee…" value={empSearch} onChange={(e) => setEmpSearch(e.target.value)} className="pl-9" data-testid="tp-emp-search" />
        </div>
        <Select value={roleF} onValueChange={setRoleF}>
          <SelectTrigger className="w-52" data-testid="tp-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {Object.entries(JOB_ROLE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="overflow-hidden rounded-lg border-zinc-200 shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-zinc-100 bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
              <th className="px-4 py-3 text-left font-medium">Employee</th>
              <th className="px-4 py-3 text-center font-medium">Content Posted / Pending</th>
              <th className="px-4 py-3 text-center font-medium">Design Done / WIP</th>
              <th className="px-4 py-3 text-center font-medium">Video Done / WIP</th>
              <th className="px-4 py-3 text-center font-medium">Publishing</th>
              <th className="px-4 py-3 text-center font-medium">Tasks Done / Overdue</th>
            </tr></thead>
            <tbody>
              {employees.map((e) => (
                <tr key={e.user_id} className="border-b border-zinc-50 hover:bg-zinc-50" data-testid={`tp-emp-${e.user_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold text-white" style={{ background: e.color || "#71717a" }}>{(e.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("")}</span>
                      <div><div className="font-medium">{e.name}</div><div className="text-xs text-zinc-400">{JOB_ROLE_LABEL[e.job_role] || e.job_role}</div></div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-emerald-600 ${cbtn}`} onClick={() => openDrill({ key: `c-posted-${e.user_id}`, title: `${e.name} · Posted content`, url: "/content", params: { posted_by_id: e.user_id }, kind: "content" })}>{e.content.posted}</span>
                    <span className="text-zinc-300"> / </span>
                    <span className={`text-amber-600 ${cbtn}`} onClick={() => openDrill({ key: `c-pending-${e.user_id}`, title: `${e.name} · Assigned content`, url: "/content", params: { assigned_to: e.user_id }, kind: "content" })}>{e.content.pending}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="font-semibold text-emerald-600">{e.design.completed}</span><span className="text-zinc-300"> / </span><span className="font-semibold text-orange-600">{e.design.in_progress}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="font-semibold text-emerald-600">{e.video.completed}</span><span className="text-zinc-300"> / </span><span className="font-semibold text-orange-600">{e.video.in_progress}</span>
                  </td>
                  <td className="px-4 py-3 text-center text-zinc-600">{e.publishing.posted} posts · {e.publishing.stories} stories</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-emerald-600 ${cbtn}`} onClick={() => openDrill({ key: `t-${e.user_id}`, title: `${e.name} · Tasks`, url: "/tasks", params: { assigned_to: e.user_id }, kind: "task" })}>{e.tasks.completed}</span>
                    <span className="text-zinc-300"> / </span>
                    <span className="font-semibold text-red-600">{e.tasks.overdue}</span>
                  </td>
                </tr>
              ))}
              {employees.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-zinc-400">No employees match</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <DrillDialog open={!!drill} onOpenChange={(v) => !v && setDrill(null)} config={drill} />
    </div>
  );
}
